import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, CheckCircle2, Calendar, Sparkles, Wrench, Tag } from "lucide-react";
import {
  buildSolutionsIndex,
  filterByTool,
  filterByCategory,
  sortByRecency,
  countTagsAcross,
} from "@/lib/browse";
import { AI_TOOLS, CATEGORIES } from "@/data/mockPeople";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const publicAxios = axios.create({ baseURL: `${BACKEND}/api` });

const formatDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};

export default function BrowseTagPage({ mode }) {
  // mode is "tool" or "category"
  const { name } = useParams();
  const decoded = decodeURIComponent(name || "");
  const [realClosed, setRealClosed] = useState([]);

  useEffect(() => {
    publicAxios
      .get("/browse/closed-problems")
      .then((r) => setRealClosed(r.data || []))
      .catch(() => setRealClosed([]));
  }, []);

  const index = useMemo(() => buildSolutionsIndex(realClosed), [realClosed]);
  const matched = useMemo(() => {
    const fn = mode === "tool" ? filterByTool : filterByCategory;
    return sortByRecency(fn(index, decoded));
  }, [index, decoded, mode]);

  // Related tags: co-occurring tools/cats within the matched set
  const related = useMemo(() => {
    const c = countTagsAcross(matched);
    if (mode === "tool") {
      return c.categories.slice(0, 6);
    }
    return c.tools.slice(0, 8);
  }, [matched, mode]);

  const backTo = "/closed-problems";
  const Accent = mode === "tool" ? Wrench : Tag;

  const isKnown =
    mode === "tool"
      ? AI_TOOLS.some((t) => t.toLowerCase() === decoded.toLowerCase())
      : CATEGORIES.some((c) => c.toLowerCase() === decoded.toLowerCase());

  return (
    <div className="max-w-6xl mx-auto px-6 lg:px-10 pt-10 pb-24">
      <Link
        to={backTo}
        className="inline-flex items-center gap-1.5 text-sm text-[#4B5563] hover:text-[#111827]"
      >
        <ArrowLeft size={16} />
        Browse all answered
      </Link>

      <header className="mt-6 flex items-start gap-5">
        <span className="w-14 h-14 rounded-2xl bg-[#D6FFEB] text-[#3FFFB6] flex items-center justify-center shrink-0">
          <Accent size={26} strokeWidth={2.2} />
        </span>
        <div className="min-w-0">
          <p className="text-xs font-semibold uppercase tracking-wider text-[#9CA3AF]">
            {mode === "tool" ? "Tool / Model" : "Category"}
          </p>
          <h1
            data-testid="browse-heading"
            className="mt-1 font-display font-bold text-4xl sm:text-5xl leading-tight text-[#111827] break-words"
          >
            {decoded}
          </h1>
          <p className="mt-3 text-[#4B5563] text-base sm:text-lg leading-relaxed max-w-2xl">
            {mode === "tool"
              ? `Real problems solved using ${decoded} on Oiiister. Learn what it's good for by seeing what it actually shipped.`
              : `Problems solved in ${decoded}. See who's shipping in this space and what worked.`}
          </p>
          <p className="mt-3 text-sm text-[#9CA3AF]">
            {matched.length} {matched.length === 1 ? "solution" : "solutions"}
            {!isKnown && " · Unknown tag — showing anything that mentions it"}
          </p>
        </div>
      </header>

      {related.length > 0 && (
        <section className="mt-10">
          <p className="text-xs font-semibold uppercase tracking-wider text-[#9CA3AF] mb-3">
            {mode === "tool" ? "Applied to categories" : "Commonly solved with"}
          </p>
          <div className="flex flex-wrap gap-2">
            {related.map((r) => (
              <Link
                key={r.name}
                data-testid={`related-${r.name}`}
                to={
                  mode === "tool"
                    ? `/categories/${encodeURIComponent(r.name)}`
                    : `/tools/${encodeURIComponent(r.name)}`
                }
                className="inline-flex items-center gap-1.5 px-3 h-8 rounded-full ring-1 ring-[#E5E7EB] bg-white text-sm text-[#111827] hover:ring-[#3FFFB6] hover:text-[#3FFFB6] transition-colors"
              >
                {r.name}
                <span className="text-[10px] text-[#9CA3AF]">{r.count}</span>
              </Link>
            ))}
          </div>
        </section>
      )}

      <section
        data-testid="browse-solutions"
        className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6"
      >
        {matched.length === 0 ? (
          <div className="col-span-full text-center py-16">
            <span className="inline-flex w-14 h-14 rounded-full bg-[#F3F4F6] text-[#9CA3AF] items-center justify-center">
              <Sparkles size={22} />
            </span>
            <p className="mt-4 font-display font-semibold text-lg text-[#111827]">
              Nothing solved with {decoded} yet.
            </p>
            <p className="mt-1 text-sm text-[#4B5563] max-w-md mx-auto">
              Be the first to ship a solution here. Post a problem or add a solution to your profile mentioning{" "}
              <span className="font-medium text-[#111827]">{decoded}</span>.
            </p>
            <Link
              to="/problems"
              className="mt-6 inline-flex items-center h-11 px-5 rounded-full bg-[#3FFFB6] hover:bg-[#2DE59F] text-[#111827] text-sm font-semibold"
            >
              Browse open problems
            </Link>
          </div>
        ) : (
          matched.map((s) => <SolutionCard key={s.id} s={s} />)
        )}
      </section>
    </div>
  );
}

function SolutionCard({ s }) {
  const inner = (
    <>
      <div className="flex items-center justify-between">
        <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-[#F3F4F6] text-[#4B5563]">
          {s.category || "Uncategorised"}
        </span>
        <span className="inline-flex items-center gap-1 text-xs font-semibold text-[#065F46]">
          <CheckCircle2 size={12} /> Solved
        </span>
      </div>
      <h3 className="mt-4 font-display font-semibold text-lg text-[#111827] leading-snug">
        {s.title}
      </h3>
      <p className="mt-2 text-sm text-[#4B5563] line-clamp-3">{s.summary}</p>
      <div className="mt-4 flex flex-wrap gap-1.5">
        {[...(s.models || []), ...(s.tools || [])].slice(0, 5).map((m) => (
          <span
            key={m}
            className="px-2 py-0.5 rounded-full text-[11px] bg-blue-50 text-blue-700"
          >
            {m}
          </span>
        ))}
      </div>
      <div className="mt-5 pt-4 border-t border-[#F3F4F6] flex items-center justify-between text-xs text-[#9CA3AF]">
        <span>By {s.solved_by_name}</span>
        <span className="inline-flex items-center gap-1">
          <Calendar size={11} /> {formatDate(s.solved_at)}
        </span>
      </div>
      {s.isDemo && (
        <p className="mt-3 inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider bg-[#F3F4F6] text-[#4B5563]">
          Demo
        </p>
      )}
    </>
  );

  const cls =
    "block bg-white rounded-3xl p-6 ring-1 ring-[#F3F4F6] hover:ring-[#D1FAE5] card-lift";

  if (s.problem_id) {
    return (
      <Link
        to={`/problems/${s.problem_id}`}
        data-testid={`browse-card-${s.problem_id}`}
        className={cls}
      >
        {inner}
      </Link>
    );
  }
  if (s.solved_by_slug) {
    return (
      <Link
        to={`/people/${s.solved_by_slug}`}
        data-testid={`browse-card-mock-${s.id}`}
        className={cls}
      >
        {inner}
      </Link>
    );
  }
  return (
    <div data-testid={`browse-card-${s.id}`} className={cls}>
      {inner}
    </div>
  );
}
